import { useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, Trash2, Star } from "lucide-react";
import { api, errDetail, imageUrl } from "@agent/lib/api";

export function VisualPanel({ draft, onDraftUpdate }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("nano-banana");
  const [genLoading, setGenLoading] = useState(false);
  const fileRef = useRef(null);
  const imageIds = draft.image_ids || [];

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the visual first"); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/images/generate", { prompt, model, draft_id: draft.id });
      onDraftUpdate({ ...draft, image_ids: [...imageIds, data.id] });
      toast.success("Visual generated");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setGenLoading(false);
    }
  };

  const upload = async (files) => {
    let current = [...imageIds];
    let successCount = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("draft_id", draft.id);
      try {
        const { data } = await api.post("/images/upload", form);
        current = [...current, data.id];
        successCount += 1;
        onDraftUpdate({ ...draft, image_ids: current });
      } catch (e) {
        toast.error(errDetail(e));
      }
    }
    if (successCount > 0) {
      toast.success(successCount > 1 ? `${successCount} images uploaded` : "Image uploaded");
    }
  };

  const removeVisual = async (imageId) => {
    const next = imageIds.filter((id) => id !== imageId);
    try {
      await api.put(`/drafts/${draft.id}`, { image_ids: next });
      onDraftUpdate({ ...draft, image_ids: next });
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] tracking-[0.2em] uppercase text-slate-400 font-semibold">Visuals {imageIds.length > 0 && `(${imageIds.length})`}</p>
      {imageIds.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="visual-gallery">
          {imageIds.map((id, i) => (
            <div key={id} className="relative border border-slate-200 rounded-xl overflow-hidden group">
              <img src={imageUrl(id)} alt="visual" className="w-full h-24 object-cover" />
              {i === 0 && (
                <span className="absolute bottom-1 left-1 bg-white/90 rounded-full px-2 py-0.5 text-[10px] font-semibold text-indigo-600 flex items-center gap-1">
                  <Star size={10} className="fill-indigo-600" /> Cover
                </span>
              )}
              <button data-testid={`remove-visual-btn-${id}`} onClick={() => removeVisual(id)}
                className="absolute top-1 right-1 bg-white/90 border border-slate-200 rounded-full p-1 text-slate-500 hover:text-rose-500 transition-colors duration-150 shadow-sm">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <textarea data-testid="visual-prompt-input" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)}
        placeholder="Describe the hero graphic / diagram to generate…"
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none" />
      <div className="flex gap-2">
        <select data-testid="visual-model-select" value={model} onChange={(e) => setModel(e.target.value)}
          className="bg-white border border-slate-200 rounded-full px-3 py-2 text-xs font-medium text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-indigo-400">
          <option value="nano-banana">Nano Banana</option>
          <option value="gpt-image-1">GPT Image 1</option>
        </select>
        <button data-testid="generate-visual-btn" onClick={generate} disabled={genLoading}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-xs font-semibold hover:opacity-90 transition-opacity duration-150 disabled:opacity-40">
          <Sparkles size={13} /> {genLoading ? "Generating…" : "Generate"}
        </button>
        <button data-testid="upload-visual-btn" onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors duration-150">
          <Upload size={13} /> Add image{imageIds.length > 0 ? "s" : ""}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden"
        data-testid="visual-file-input"
        onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) upload(files); e.target.value = ""; }} />
    </div>
  );
}
